import { User } from './User';
import { Listing, ListingEnquiry } from './Listing';
import { DropzoneFile } from 'components/Dropzone/utils';
import { NdaStatus } from './Nda';
import {
  DataRoomNode,
  DataRoomNodeTag,
} from 'router/subrouters/DataRoom/models/DataRoomNode';
import { ObjectValues } from 'types';
import { DealRoom } from './DealRoom';
import { EnquiryQuestionType } from 'router/subrouters/Dash/subrouters/Business/components/EnquirySlider/utils';

export type LeadState =
  | 'enquire'
  | 'enquireWaitlisted'
  | 'accept'
  | 'acceptWaitlisted'
  | 'confirm'
  | 'confirmWaitlisted'
  | 'offer'
  | 'offerWaitlisted';

export type WaitlistedStages =
  | 'enquireWaitlisted'
  | 'acceptWaitlisted'
  | 'confirmWaitlisted'
  | 'offerWaitlisted';

export const LEAD_STAGES = {
  ENQUIRE: 'enquire',
  ACCEPT: 'accept',
  CONFIRM: 'confirm',
  OFFER: 'offer',
} as const;
export type LeadStages = ObjectValues<typeof LEAD_STAGES>;

export const LEAD_STAGES_COPY = {
  ENQUIRY: 'enquiry',
  ACCEPTED: 'accepted',
  PROSPECT: 'prospect',
  BIDDER: 'bidder',
} as const;
export type LeadStagesCopy = ObjectValues<typeof LEAD_STAGES_COPY>;

export type Lead = {
  _id: string;

  // states as tags
  withdrawnInterest?: boolean; // Withdrawn
  isExternal?: boolean; // Added
  isPrivateViewer?: boolean; // Invited
  isRequestedPrivateViewer?: boolean; // Requested
  confirmedInterest?: boolean; // Confirmed
  isOfferRejected?: boolean; // Offer rejected
  isOfferAccepted?: boolean; // Offer accepted
  isInactive?: boolean; // Didn't confirm access

  isManualWaitlisted?: boolean;
  sessionId: string;
  progressedStep: number;
  firstName: string;
  lastName: string;
  email: string;
  contactNumber?: string;
  buyerId: string;
  sellerId: string;
  leadState: LeadState;
  buyerStep: LeadStages;
  listingEnquiryId: string;
  listingId: string;
  changeLeadStateDate: Date;
  index: number;
  buyer?: Partial<User>;
  listingEnquiry?: Partial<ListingEnquiry> | null;
  seller?: Partial<User> | null;
  listing?: Partial<Listing> | null;
  NBIO: DropzoneFile;
  enquiryQuestions: EnquiryQuestionType[];
  dataRoomMemberId?: string;
  canManageDataRoom?: boolean; // true if user who fetch lead's stat is data room admin or owner
  numberOfAccessableDocuments: number;
  referrerName: string;
  createdAt: Date;
  company?: string;
  linkedinProfile?: string;
  natureOfInterest?: string;
  howDiscoverListing?: string;
  dealRoom?: Partial<DealRoom>;
  hasDataRoomAccess?: boolean;
  brokerViewIds: string[];

  context?: string;

  // the most important X number of data room nodes
  dataRoomTopNodes: DataRoomNodeIdWithStateAndSignal[];

  // chat
  lastSeen?: Date;
  unreadMessagesCount?: number;
  unrepliedMessagesCount?: number;

  // notes
  unreadNotesCount?: number;
  unrepliedNotesCount?: number;

  ndaSigningStatus?: NdaStatus;
  ndaFile: DropzoneFile;

  //virtual props
  isDataRoomFile: boolean;
  canMoveFileToDataRoom: boolean;
  dataRoomNodeId: string;

  manuallySetNDAStatus?: boolean;
  manuallySetNDAStatusDate?: Date;
  manuallySetNDAStatusUser?: Partial<User>;

  withdrawnInterestDate?: Date;

  pulseCheckActive?: boolean;
  softSignal?: SoftSignal;
  softSignalAt?: Date;
} & LeadNdaProps;

export type LeadNdaProps = {
  envelopeId?: string; // this is Lead's NDA
  envelopeTemplateId?: string; // NDA exists for that deal
  signingStatus?: NdaStatus;
  sentDate?: Date;
  signedDate?: Date;
  rejectionReason?: string;
};

export const DATA_ROOM_NODE_ACCESS_STATES = {
  NO_ACCESS: 'no-access',
  NOT_VIEWED: 'not-viewed',
  VIEWED: 'viewed',
} as const;

type DataRoomNodeAccessState = ObjectValues<
  typeof DATA_ROOM_NODE_ACCESS_STATES
>;

export const SOFT_SIGNALS = {
  LIKE: 'like',
  UNSURE: 'unsure',
  DISLIKE: 'dislike',
} as const;

export type SoftSignal = ObjectValues<typeof SOFT_SIGNALS>;

export type SoftSignalPayload = {
  softSignal: SoftSignal;
  message?: string;
  withdrawInterest?: boolean;
};

export type DataRoomNodeWithStateAndSignal =
  DataRoomNodeIdWithStateAndSignal & {
    dataRoomNode: DataRoomNode;
  };

export type DataRoomNodeIdWithStateAndSignal = {
  dataRoomNodeId: string;
  state: DataRoomNodeAccessState;
  softSignal?: SoftSignal;
  softSignalAt?: Date;
};

export type BuyersActivityTrack = {
  _id: string;

  createdAt: Date;

  eventType: BuyersActivityTrackEvents;

  userId: string;
  user: Partial<User> | null;

  dealRoomId: string;
  dealRoom: Partial<DealRoom> | null;

  leadId: string;
  lead: Partial<Lead> | null;

  // for file view and soft signal reaction events
  dataRoomNodeId: string;
  dataRoomNode: Partial<DataRoomNode> | null;

  dataRoomNodeTag?: DataRoomNodeTag;

  oldStage?: LeadStages & 'withdraw';
  newStage?: LeadStages & 'withdraw';

  softSignal?: SoftSignal;

  progressedByUserId: string;
  progressedByUser: Partial<User> | null;
};

export const BUYERS_ACTIVITY_TRACK_EVENTS = {
  LISTING_ENQUIRED: 'listing_enquired',
  VIEWED_TEASER: 'viewed_teaser',
  SENT_NDA: 'sent_nda',
  RESENT_NDA: 'resent_nda',
  SIGNED_NDA: 'signed_nda',
  VIEWED_FILE: 'viewed_file',
  CONFIRMED_INTEREST: 'confirmed_interest',
  WITHDRAWN_INTEREST: 'withdrawn_interest',
  RELEASED_TAGGED_FILE: 'released_tagged_file',
  VIEWED_TAGGED_FILE: 'viewed_tagged_file',
  MANUALLY_PROGRESSED: 'manually_progressed',
  AUTOMATICALLY_PROGRESSED: 'automatically_progressed',
  FILE_SOFT_SIGNAL: 'file_soft_signal',
  PULSE_CHECK_SENT: 'pulse_check_sent',
  PULSE_CHECK_REACTION: 'pulse_check_reaction',
  SUBMIT_OFFER: 'submit_offer',
  OFFER_ACCEPTED: 'offer_accepted',
  OFFER_REJECTED: 'offer_rejected',
} as const;

export type BuyersActivityTrackEvents = ObjectValues<
  typeof BUYERS_ACTIVITY_TRACK_EVENTS
>;

export const BUYERS_ACTIVITY_TRACK_EVENTS_COPY: Record<
  BuyersActivityTrackEvents,
  string
> = {
  [BUYERS_ACTIVITY_TRACK_EVENTS.LISTING_ENQUIRED]: 'listingEnquired',
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_TEASER]: 'viewedTeaser',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SENT_NDA]: 'sentNDA',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RESENT_NDA]: 'ndaResent',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SIGNED_NDA]: 'signedNDA',
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_FILE]: 'viewedFile',
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST]: 'confirmedInterest',
  [BUYERS_ACTIVITY_TRACK_EVENTS.WITHDRAWN_INTEREST]: 'withdrawnInterest',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASED_TAGGED_FILE]: 'releasedTaggedFile',
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_TAGGED_FILE]: 'viewedTaggedFile',
  [BUYERS_ACTIVITY_TRACK_EVENTS.MANUALLY_PROGRESSED]:
    'leadManuallyProgressedFromTo',
  [BUYERS_ACTIVITY_TRACK_EVENTS.AUTOMATICALLY_PROGRESSED]:
    'leadAutomaticallyProgressedFromTo',
  [BUYERS_ACTIVITY_TRACK_EVENTS.FILE_SOFT_SIGNAL]: 'fileSoftSignal',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PULSE_CHECK_SENT]: 'pulseCheckSent',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PULSE_CHECK_REACTION]: 'pulseCheckReaction',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SUBMIT_OFFER]: 'offerSubmitted',
  [BUYERS_ACTIVITY_TRACK_EVENTS.OFFER_ACCEPTED]: 'offerAccepted',
  [BUYERS_ACTIVITY_TRACK_EVENTS.OFFER_REJECTED]: 'offerRejected',
};
